import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSlots, bookAppointment, PracticeInfo, Slot } from '../api/bookingApi';
import LoadingState from './LoadingState';

interface SlotPickerStepProps {
  clientId: string;
  patientToken: string;
  practiceInfo: PracticeInfo | null;
  patientName: string;
  apptType: string;
  onBooked: (r: { slot: Slot; apptType: string; confirmation: unknown }) => void;
  onSessionExpired: () => void;
  onBack: () => void;
}

// Local calendar date, NOT toISOString(). toISOString() converts to UTC first, so
// for any timezone behind UTC an evening visitor got tomorrow's date — "today" was
// disabled in the picker and the default selection skipped a bookable day.
function isoDate(d: Date): string {
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

// Inverse of isoDate. `new Date('2026-08-12')` parses as UTC midnight and renders
// as the day *before* in western timezones, so build the date from its parts.
function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function SlotPickerStep({ clientId, patientToken, practiceInfo, patientName, apptType, onBooked, onSessionExpired, onBack }: SlotPickerStepProps) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedDate, setSelectedDate] = useState(isoDate(new Date()));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState('');
  // Set when the backend fails closed (403 — catalog went non-ready after page load):
  // the server's message is already patient-safe, but "call the office" needs a number.
  const [callOfficePhone, setCallOfficePhone] = useState<string | null>(null);
  // Where to look when this day is empty — the backend's forward scan, so the
  // patient doesn't have to open each date to find out there's nothing there.
  const [nextAvailableDate, setNextAvailableDate] = useState<string | null>(null);

  const fetchSlots = useCallback(async () => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    setSlotsError('');
    setCallOfficePhone(null);
    setSlots([]);
    setNextAvailableDate(null);
    setSelectedSlotId(null);
    try {
      const data = await getSlots(clientId, patientToken, { appointment_type: apptType, date: selectedDate });
      let slotList = Array.isArray(data) ? data : (data.slots ?? []);
      const hint = Array.isArray(data) ? null : (data.next_available_date ?? null);

      // Filter out past slots for today
      const now = new Date();
      slotList = slotList.filter(slot => {
        if (!slot.datetime_iso) return true; // Fallback if no iso provided
        const slotTime = new Date(slot.datetime_iso);
        return slotTime > now;
      });

      setSlots(slotList);
      // Ignore a hint pointing at the day we're already on (nothing to jump to)
      // and any backward-pointing one, so the empty state can't offer a no-op.
      setNextAvailableDate(hint && hint > selectedDate ? hint : null);
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 401) { onSessionExpired(); return; }
      if (apiErr.status === 403) setCallOfficePhone(practiceInfo?.phone_number ?? null);
      setSlotsError(apiErr.message || 'Could not load available times.');
    } finally {
      setSlotsLoading(false);
    }
  }, [clientId, patientToken, apptType, selectedDate, onSessionExpired, practiceInfo?.phone_number]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // Move the picker to `iso`, pulling the visible week along when it's outside it.
  function jumpToDate(iso: string) {
    setWeekStart(startOfWeek(parseIsoDate(iso)));
    setSelectedDate(iso);
  }

  async function handleBook() {
    if (selectedSlotId === null) return;
    setBooking(true);
    setBookError('');
    setCallOfficePhone(null);
    try {
      const slot = slots[selectedSlotId];
      if (!slot) return;
      const result = await bookAppointment(clientId, patientToken, slot, apptType);
      if (result.success === false) {
        setBookError(result.message || 'Booking failed. Please try again.');
        setBooking(false);
        return;
      }
      onBooked({ slot, apptType, confirmation: result });
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      setBooking(false);
      if (apiErr.status === 401) { onSessionExpired(); return; }
      if (apiErr.status === 403) setCallOfficePhone(practiceInfo?.phone_number ?? null);
      setBookError(apiErr.message || 'Booking failed. Please try again.');
    }
  }

  const today = isoDate(new Date());
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  function formatTime(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  function formatLongDate(iso: string): string {
    return parseIsoDate(iso).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  return (
    <AnimatePresence mode="wait">
      {booking ? (
        <LoadingState key="loading" type="booking" message="Securing Appointment" />
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Choose a time</h2>
            {patientName && <p className="text-gray-500 mt-1">Hi {patientName}!</p>}
          </div>

          {/* Week calendar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <button
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setWeekStart((w) => addDays(w, -7))}
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-medium text-gray-700">
                {MONTH_NAMES[weekDays[0].getMonth()]} {weekDays[0].getDate()} –{' '}
                {MONTH_NAMES[weekDays[6].getMonth()]} {weekDays[6].getDate()},{' '}
                {weekDays[6].getFullYear()}
              </span>
              <button
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setWeekStart((w) => addDays(w, 7))}
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {weekDays.map((d) => {
                const iso = isoDate(d);
                const isPast = iso < today;
                const isSelected = iso === selectedDate;
                return (
                  <button
                    key={iso}
                    disabled={isPast}
                    onClick={() => setSelectedDate(iso)}
                    className={`flex flex-col items-center py-2 rounded-lg text-[10px] sm:text-xs transition-colors ${
                      isSelected
                        ? 'bg-primary-deep text-white'
                        : isPast
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'hover:bg-muted text-foreground'
                    }`}
                  >
                    <span className="font-medium">{DAY_LABELS[d.getDay()]}</span>
                    <span className={`mt-0.5 text-xs sm:text-sm font-semibold ${isSelected ? '' : 'text-gray-900'} ${isPast ? 'text-gray-300' : ''}`}>
                      {d.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time slots */}
          <div className="min-h-[120px]">
            {slotsLoading && (
              <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
                Loading available times…
              </div>
            )}
            {slotsError && (
              <div className="text-center py-4">
                <p className="error-text">{slotsError}</p>
                {callOfficePhone && (
                  <a
                    href={`tel:${callOfficePhone.replace(/[^\d+]/g, '')}`}
                    className="inline-block mt-2 text-base font-semibold text-sky-600 hover:text-sky-700"
                  >
                    {callOfficePhone}
                  </a>
                )}
              </div>
            )}
            {!slotsLoading && !slotsError && slots.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">
                  No available times on this date.
                </p>
                {nextAvailableDate ? (
                  <button
                    type="button"
                    onClick={() => jumpToDate(nextAvailableDate)}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-primary text-primary-deep hover:bg-primary-deeper hover:text-white transition-colors"
                  >
                    <span>Next opening: {formatLongDate(nextAvailableDate)}</span>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <p className="text-gray-500 text-sm mt-1">Try another day.</p>
                )}
              </div>
            )}
            {!slotsLoading && slots.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {slots.map((slot, i) => (
                  <button
                    key={slot.slot_id || i}
                    onClick={() => setSelectedSlotId(i)}
                    className={`py-2.5 px-2 text-sm font-medium rounded-lg border transition-colors ${
                      selectedSlotId === i
                        ? 'bg-primary-deep border-primary text-white'
                        : 'bg-card border-border text-foreground hover:border-primary hover:text-primary-deep'
                    }`}
                  >
                    {formatTime(slot.datetime_iso || slot.time)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {bookError && (
            <div className="mt-3 text-center">
              <p className="error-text">{bookError}</p>
              {callOfficePhone && (
                <a
                  href={`tel:${callOfficePhone.replace(/[^\d+]/g, '')}`}
                  className="inline-block mt-1 text-base font-semibold text-sky-600 hover:text-sky-700"
                >
                  {callOfficePhone}
                </a>
              )}
            </div>
          )}

          <button
            className="btn-primary mt-4 w-full"
            disabled={selectedSlotId === null || booking}
            onClick={handleBook}
          >
            Confirm appointment
          </button>

          <button
            type="button"
            className="btn-secondary mt-3"
            onClick={onBack}
          >
            Change appointment type
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
